import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentCompany } from "@/hooks/useCurrentCompany";

export interface HarvestComponentRow {
  id: string;
  name: string;
  description: string | null;
  category: string | null;
  material: string | null;
  default_weight_kg: number;
  default_value: number;
  default_recovery_pct: number;
  is_hazardous: boolean;
  appliance_category: string | null;
  is_system_default: boolean;
  company_id: string | null;
}

/**
 * IDs of system-default harvest components that the caller's company has
 * disabled in Harvesting Management. RLS already scopes by company.
 */
export function useCompanyDisabledHarvestComponentIds() {
  return useQuery({
    queryKey: ["company_disabled_harvest_components"],
    queryFn: async (): Promise<Set<string>> => {
      const { data, error } = await supabase
        .from("company_disabled_harvest_components")
        .select("component_id");
      if (error) throw error;
      return new Set((data || []).map((r: any) => r.component_id as string));
    },
  });
}

export function useHarvestComponents() {
  return useQuery({
    queryKey: ["harvest_components"],
    queryFn: async (): Promise<HarvestComponentRow[]> => {
      const { data, error } = await supabase
        .from("harvest_components")
        .select("*")
        .order("is_system_default", { ascending: false })
        .order("name");
      if (error) throw error;
      return (data || []) as HarvestComponentRow[];
    },
  });
}

/**
 * Returns harvest components available for a given appliance category:
 * system defaults + the caller's company-custom rows, minus any system
 * defaults the company has explicitly disabled.
 */
export function useHarvestComponentsForApplianceCategory(applianceCategory?: string | null) {
  return useQuery({
    queryKey: ["harvest_components_for_appliance", applianceCategory],
    enabled: !!applianceCategory,
    queryFn: async (): Promise<HarvestComponentRow[]> => {
      const [{ data, error }, { data: disabled, error: dErr }] = await Promise.all([
        supabase
          .from("harvest_components")
          .select("*")
          .eq("appliance_category", applianceCategory!)
          .order("is_system_default", { ascending: false })
          .order("name"),
        supabase
          .from("company_disabled_harvest_components")
          .select("component_id"),
      ]);
      if (error) throw error;
      if (dErr) throw dErr;
      const disabledSet = new Set((disabled || []).map((r: any) => r.component_id as string));
      return ((data || []) as HarvestComponentRow[]).filter(
        (c) => !(c.is_system_default && disabledSet.has(c.id))
      );
    },
  });
}

/**
 * Effective harvestable components for brand × appliance type in a company:
 * (platform defaults ∪ company includes) − company excludes − company-disabled.
 * Resolved server-side by `effective_brand_harvest_components` (single resolver).
 *
 * The company is the appliance's company when `applianceId` is a DB appliance,
 * otherwise the user's active company.
 */
export function useHarvestComponentsForBrand(
  brand?: string | null,
  applianceType?: string | null,
  applianceCategoryLabel?: string | null,
  applianceId?: string | null,
) {
  const { id: activeCompanyId } = useCurrentCompany();
  return useQuery({
    queryKey: ["harvest_components_for_brand", brand, applianceType, applianceCategoryLabel, applianceId, activeCompanyId],
    queryFn: async (): Promise<HarvestComponentRow[]> => {
      let companyId = activeCompanyId ?? null;
      if (applianceId && /^[0-9a-f-]{36}$/i.test(applianceId)) {
        const { data: app } = await supabase.from("appliances").select("company_id").eq("id", applianceId).maybeSingle();
        if (app?.company_id) companyId = app.company_id;
      }
      let query = supabase.from("harvest_components").select("*").order("name");
      if (applianceCategoryLabel) query = query.eq("appliance_category", applianceCategoryLabel);
      const { data: all, error: allErr } = await query;
      if (allErr) throw allErr;
      const rows = (all || []) as HarvestComponentRow[];

      if (!brand || !companyId) {
        const { data: disabled, error: dErr } = await supabase
          .from("company_disabled_harvest_components")
          .select("component_id");
        if (dErr) throw dErr;
        const disabledSet = new Set((disabled || []).map((r: any) => r.component_id as string));
        return rows.filter((c) => !(c.is_system_default && disabledSet.has(c.id)));
      }

      const { data: eff, error: effErr } = await supabase.rpc("effective_brand_harvest_components", {
        _company_id: companyId,
        _brand: brand,
        _appliance_type: (applianceType || applianceCategoryLabel || null) as any,
      });
      if (effErr) throw effErr;
      const allowed = new Set((eff || []).map((r: any) => r.component_id as string));
      return rows.filter((c) => allowed.has(c.id));
    },
  });
}
